import type { ReactNode } from "react";
import { EmptyState, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useFunnel, useSla } from "@/api/hooks/useAnalytics";
import { FUNNEL_STAGE_LABELS, type FunnelStage } from "@/api/types/analytics";

/**
 * V2 Builder 5 — lead-to-cash funnel + response-time SLA
 * (docs/V2_FEATURES_PLAN.md). Reads GET /v2/analytics/funnel (stage
 * counts, stage-to-stage conversion %, open-pipeline forecast) and
 * GET /v2/analytics/sla (reply-time distribution vs. the 2h SLA). Every
 * number is server-computed (app/services/funnel.py); this page only
 * formats and lays out a horizontal step chart — no chart dependency, an
 * inline set of divs sized by each stage's count relative to the funnel's
 * first stage.
 *
 * Not yet reachable via in-app navigation (App.tsx/AppShell.tsx are out
 * of this builder's scope, see docs/V2_FEATURES_PLAN.md's orchestrator
 * integration step) — the component and its route target
 * (`/analytics/funnel`, wired by the orchestrator) are complete and
 * self-contained.
 */
export function FunnelPage() {
  const { data: funnelData, isLoading: funnelLoading, error: funnelError, refetch: refetchFunnel } =
    useFunnel();
  const { data: slaData, isLoading: slaLoading, error: slaError, refetch: refetchSla } = useSla();

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
          Analytics
        </p>
        <h1 className="mt-1 font-display text-2xl font-bold text-text-primary">
          Lead-to-cash funnel
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Where leads drop off between the first inquiry and a paid invoice, plus how fast we
          reply.
        </p>
      </div>

      <div className="flex flex-col gap-6">
        <QueryStateBoundary
          isLoading={funnelLoading}
          error={funnelError}
          onRetry={() => void refetchFunnel()}
        >
          {funnelData && <FunnelCard funnel={funnelData} />}
        </QueryStateBoundary>

        <QueryStateBoundary isLoading={slaLoading} error={slaError} onRetry={() => void refetchSla()}>
          {slaData && (
            <SectionCard title="Response-time SLA" testId="sla-card">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <SlaTile
                  label="Median reply time"
                  value={formatMinutes(slaData.median_response_minutes)}
                />
                <SlaTile label="P90 reply time" value={formatMinutes(slaData.p90_response_minutes)} />
                <SlaTile
                  label="Within 2h SLA"
                  value={`${slaData.pct_within_sla}%`}
                  emphasize={slaData.pct_within_sla < 90}
                />
                <SlaTile
                  label="Answered"
                  value={`${slaData.answered_count} / ${slaData.total_inquiries}`}
                />
              </div>
            </SectionCard>
          )}
        </QueryStateBoundary>
      </div>
    </div>
  );
}

// ---- Section shell (mirrors DashboardPage's SectionCard) -----------------------

function SectionCard({
  title,
  sub,
  aside,
  testId,
  children,
}: {
  title: string;
  sub?: string;
  aside?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-xl border border-border bg-bg-card p-5 shadow-sm sm:p-6"
      data-testid={testId}
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-text-secondary">
            {title}
          </h2>
          {sub && <p className="mt-1 text-xs text-text-muted">{sub}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

// ---- Funnel step chart -----------------------------------------------------------

function FunnelCard({ funnel }: { funnel: { stages: FunnelStage[]; forecast_cents: number } }) {
  const { stages, forecast_cents } = funnel;

  if (stages.length === 0 || stages[0].count === 0) {
    return (
      <SectionCard title="Funnel" testId="funnel-card">
        <EmptyState
          icon="🔻"
          title="No inquiries yet"
          description="The funnel fills in as inquiries, quotes, events, and invoices happen."
        />
      </SectionCard>
    );
  }

  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <SectionCard
      title="Funnel"
      sub="Inquiry through paid invoice — width tracks count, conversion is vs. the previous stage."
      testId="funnel-card"
      aside={
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-[0.08em] text-text-muted">
            Open-pipeline forecast
          </p>
          <p className="tabular text-lg font-semibold text-text-primary" data-testid="funnel-forecast">
            {centsToUsd(forecast_cents)}
          </p>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {stages.map((stage) => {
          const pct = maxCount > 0 ? Math.round((stage.count / maxCount) * 100) : 0;
          const label = FUNNEL_STAGE_LABELS[stage.name] ?? stage.name;

          return (
            <div key={stage.name} data-testid={`funnel-stage-${stage.name}`}>
              <div className="mb-1 flex items-baseline justify-between text-xs">
                <span className="font-medium text-text-primary">{label}</span>
                <span className="tabular text-text-muted">
                  {stage.count}
                  {stage.conversion_from_previous_pct !== null
                    ? ` · ${stage.conversion_from_previous_pct}% of previous`
                    : ""}
                </span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-bg-muted">
                <div
                  className="h-full rounded-full bg-copper transition-[width]"
                  style={{ width: `${Math.max(pct, stage.count > 0 ? 3 : 0)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ---- SLA tiles -------------------------------------------------------------------

function SlaTile({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-body p-4">
      <p className="text-[11px] uppercase tracking-[0.08em] text-text-muted">{label}</p>
      <p
        className={`tabular mt-1 text-xl font-semibold ${
          emphasize ? "text-danger" : "text-text-primary"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const rem = Math.round(minutes % 60);
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}
